import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Clock, Save, Plus, Trash2, MapPin, AlertCircle } from "lucide-react";
import { PageContainer } from "@/components/pro/PageContainer";
import { PageHeader } from "@/components/pro/PageHeader";
import { format, startOfWeek, endOfWeek, getISOWeek, addWeeks } from "date-fns";
import { fr } from "date-fns/locale";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface Location {
  id: string;
  name: string;
  city: string | null;
  is_active: boolean;
}

interface TimeSlot {
  id: string;
  start_time: string;
  end_time: string;
  is_available: boolean;
  online_only: boolean;
  in_office_only: boolean;
  location_id: string | null;
}

interface DaySetting {
  day_of_week: number;
  slots: TimeSlot[];
}

const DAYS = [
  { value: 1, label: "Lundi" },
  { value: 2, label: "Mardi" },
  { value: 3, label: "Mercredi" },
  { value: 4, label: "Jeudi" },
  { value: 5, label: "Vendredi" },
  { value: 6, label: "Samedi" },
  { value: 0, label: "Dimanche" },
];

const TIME_OPTIONS = Array.from({ length: 13 }, (_, i) => {
  const hour = i + 8; // 8h à 20h
  return `${String(hour).padStart(2, "0")}:00`;
});

export default function AvailabilitySettings() {
  const navigate = useNavigate();
  const [settings, setSettings] = useState<DaySetting[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedWeek, setSelectedWeek] = useState<string>("default");
  const [weekOptions, setWeekOptions] = useState<{ value: string; label: string; weekNumber: number; year: number; startDate: string; endDate: string }[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [defaultLocationId, setDefaultLocationId] = useState<string | null>(null);

  useEffect(() => {
    generateWeekOptions();
    loadLocations();
    loadSettings();
  }, []);

  useEffect(() => {
    loadSettings();
  }, [selectedWeek]);

  const loadLocations = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from("locations")
        .select("id, name, city, is_active, is_default")
        .eq("user_id", user.id)
        .eq("is_active", true)
        .order("is_default", { ascending: false })
        .order("name");

      if (error) throw error;
      setLocations(data || []);
      
      // Find default location
      const defaultLoc = data?.find(l => (l as any).is_default);
      if (defaultLoc) {
        setDefaultLocationId(defaultLoc.id);
      }
    } catch (error) {
      console.error("Error loading locations:", error);
    }
  };

  const generateWeekOptions = () => {
    const options = [{ value: "default", label: "Horaires par défaut", weekNumber: 0, year: 0, startDate: "", endDate: "" }];
    
    // Generate weeks from now until end of 2026 (approximately 104 weeks)
    const currentDate = new Date();
    const endDate = new Date(2026, 11, 31); // December 31, 2026
    const weeksToGenerate = Math.ceil((endDate.getTime() - currentDate.getTime()) / (7 * 24 * 60 * 60 * 1000));
    
    for (let i = 0; i < weeksToGenerate; i++) {
      const date = addWeeks(currentDate, i);
      const weekNum = getISOWeek(date);
      const year = date.getFullYear();
      const start = startOfWeek(date, { weekStartsOn: 1 });
      const end = endOfWeek(date, { weekStartsOn: 1 });
      
      options.push({
        value: `${year}-W${weekNum}`,
        label: `Semaine ${weekNum} - ${year} (${format(start, "dd/MM", { locale: fr })} - ${format(end, "dd/MM/yyyy", { locale: fr })})`,
        weekNumber: weekNum,
        year: year,
        startDate: format(start, "yyyy-MM-dd"),
        endDate: format(end, "yyyy-MM-dd"),
      });
    }
    
    setWeekOptions(options);
  };

  const loadSettings = async () => {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return;

      let daySettings: DaySetting[] = [];

      if (selectedWeek === "default") {
        // Load default availability settings
        const { data, error } = await supabase
          .from("availability_settings")
          .select("*")
          .eq("user_id", user.id)
          .order("day_of_week");

        if (error) throw error;

        if (!data || data.length === 0) {
          // Initialize with default settings
          daySettings = DAYS.map((day) => ({
            day_of_week: day.value,
            slots: [{
              id: crypto.randomUUID(),
              start_time: "09:00",
              end_time: "18:00",
              is_available: day.value >= 1 && day.value <= 5,
              online_only: false,
              in_office_only: false,
              location_id: defaultLocationId,
            }],
          }));
        } else {
          // Group by day and create slots
          daySettings = DAYS.map((day) => {
            const daySlotsData = data.filter(s => s.day_of_week === day.value);
            return {
              day_of_week: day.value,
              slots: daySlotsData.length > 0 ? daySlotsData.map(s => ({
                id: s.id || crypto.randomUUID(),
                start_time: s.start_time.substring(0, 5),
                end_time: s.end_time.substring(0, 5),
                is_available: s.is_available,
                online_only: s.online_only,
                in_office_only: s.in_office_only,
                location_id: (s as any).location_id || null,
              })) : [{
                id: crypto.randomUUID(),
                start_time: "09:00",
                end_time: "18:00",
                is_available: false,
                online_only: false,
                in_office_only: false,
                location_id: defaultLocationId,
              }],
            };
          });
        }
      } else {
        // Load weekly override settings
        const weekOption = weekOptions.find(w => w.value === selectedWeek);
        if (!weekOption) return;

        const { data, error } = await supabase
          .from("weekly_availability_overrides")
          .select("*")
          .eq("user_id", user.id)
          .eq("week_number", weekOption.weekNumber)
          .eq("year", weekOption.year)
          .order("day_of_week");

        if (error) throw error;

        if (!data || data.length === 0) {
          // Initialize from default settings
          const { data: defaultData } = await supabase
            .from("availability_settings")
            .select("*")
            .eq("user_id", user.id);

          daySettings = DAYS.map((day) => {
            const defaultSlot = defaultData?.find(s => s.day_of_week === day.value);
            return {
              day_of_week: day.value,
              slots: [{
                id: crypto.randomUUID(),
                start_time: defaultSlot?.start_time.substring(0, 5) || "09:00",
                end_time: defaultSlot?.end_time.substring(0, 5) || "18:00",
                is_available: defaultSlot?.is_available || false,
                online_only: defaultSlot?.online_only || false,
                in_office_only: defaultSlot?.in_office_only || false,
                location_id: (defaultSlot as any)?.location_id || defaultLocationId,
              }],
            };
          });
        } else {
          // Group by day and create slots
          daySettings = DAYS.map((day) => {
            const daySlotsData = data.filter(s => s.day_of_week === day.value);
            return {
              day_of_week: day.value,
              slots: daySlotsData.length > 0 ? daySlotsData.map(s => ({
                id: s.id || crypto.randomUUID(),
                start_time: s.start_time.substring(0, 5),
                end_time: s.end_time.substring(0, 5),
                is_available: s.is_available,
                online_only: s.online_only,
                in_office_only: s.in_office_only,
                location_id: (s as any).location_id || null,
              })) : [{
                id: crypto.randomUUID(),
                start_time: "09:00",
                end_time: "18:00",
                is_available: false,
                online_only: false,
                in_office_only: false,
                location_id: defaultLocationId,
              }],
            };
          });
        }
      }

      setSettings(daySettings);
    } catch (error: any) {
      toast.error("Erreur lors du chargement des paramètres");
    } finally {
      setLoading(false);
    }
  };

  const updateSlot = (dayOfWeek: number, slotId: string, field: keyof TimeSlot, value: any) => {
    setSettings((prev) =>
      prev.map((daySetting) =>
        daySetting.day_of_week === dayOfWeek
          ? {
              ...daySetting,
              slots: daySetting.slots.map((slot) =>
                slot.id === slotId ? { ...slot, [field]: value } : slot
              ),
            }
          : daySetting
      )
    );
  };

  const addSlot = (dayOfWeek: number) => {
    setSettings((prev) =>
      prev.map((daySetting) =>
        daySetting.day_of_week === dayOfWeek
          ? {
              ...daySetting,
              slots: [
                ...daySetting.slots,
                {
                  id: crypto.randomUUID(),
                  start_time: "09:00",
                  end_time: "18:00",
                  is_available: false,
                  online_only: false,
                  in_office_only: false,
                  location_id: defaultLocationId,
                },
              ],
            }
          : daySetting
      )
    );
  };

  const removeSlot = (dayOfWeek: number, slotId: string) => {
    setSettings((prev) =>
      prev.map((daySetting) =>
        daySetting.day_of_week === dayOfWeek
          ? {
              ...daySetting,
              slots: daySetting.slots.filter((slot) => slot.id !== slotId),
            }
          : daySetting
      )
    );
  };

  const handleSave = async () => {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return;

      if (selectedWeek === "default") {
        // Save to availability_settings
        await supabase.from("availability_settings").delete().eq("user_id", user.id);

        const settingsToInsert = settings.flatMap((daySetting) =>
          daySetting.slots.map((slot) => ({
            user_id: user.id,
            day_of_week: daySetting.day_of_week,
            start_time: slot.start_time,
            end_time: slot.end_time,
            is_available: slot.is_available,
            online_only: slot.online_only,
            in_office_only: slot.in_office_only,
            // Save location_id for in_office_only slots, or when not online_only (mixed mode)
            location_id: slot.online_only ? null : slot.location_id,
          }))
        );

        if (settingsToInsert.length > 0) {
          const { error } = await supabase.from("availability_settings").insert(settingsToInsert);
          if (error) throw error;
        }
      } else {
        // Save to weekly_availability_overrides
        const weekOption = weekOptions.find(w => w.value === selectedWeek);
        if (!weekOption) return;

        await supabase
          .from("weekly_availability_overrides")
          .delete()
          .eq("user_id", user.id)
          .eq("week_number", weekOption.weekNumber)
          .eq("year", weekOption.year);

        const overridesToInsert = settings.flatMap((daySetting) =>
          daySetting.slots.map((slot) => ({
            user_id: user.id,
            week_number: weekOption.weekNumber,
            year: weekOption.year,
            start_date: weekOption.startDate,
            end_date: weekOption.endDate,
            day_of_week: daySetting.day_of_week,
            start_time: slot.start_time,
            end_time: slot.end_time,
            is_available: slot.is_available,
            online_only: slot.online_only,
            in_office_only: slot.in_office_only,
            location_id: slot.online_only ? null : slot.location_id,
          }))
        );

        if (overridesToInsert.length > 0) {
          const { error } = await supabase
            .from("weekly_availability_overrides")
            .insert(overridesToInsert);
          if (error) throw error;
        }
      }

      toast.success("Paramètres sauvegardés avec succès");
      await loadSettings();
    } catch (error: any) {
      toast.error("Erreur lors de la sauvegarde", {
        description: error.message,
      });
    }
  };

  const getDaySetting = (dayOfWeek: number): DaySetting => {
    return (
      settings.find((s) => s.day_of_week === dayOfWeek) || {
        day_of_week: dayOfWeek,
        slots: [{
          id: crypto.randomUUID(),
          start_time: "09:00",
          end_time: "18:00",
          is_available: false,
          online_only: false,
          in_office_only: false,
          location_id: null,
        }],
      }
    );
  };

  return (
    <PageContainer>
      <PageHeader
        title="Horaires d'ouverture"
        subtitle="Définissez vos horaires de travail par défaut ou par semaine"
        icon={<Clock className="w-6 h-6 sm:w-7 sm:h-7" />}
        iconColor="primary"
        actions={
          <Button onClick={handleSave} className="gap-2 bg-gradient-primary">
            <Save className="w-4 h-4" />
            Enregistrer
          </Button>
        }
      />

      <Card className="border-0 bg-card/80 backdrop-blur-sm shadow-card">
        <CardHeader>
          <CardTitle className="font-cinzel">Configuration de vos disponibilités</CardTitle>
          <CardDescription>
            Vous pouvez configurer des horaires par défaut ou des horaires spécifiques pour chaque semaine.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">
              <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center animate-pulse">
                <Clock className="w-6 h-6 text-primary" />
              </div>
              <p className="text-muted-foreground">Chargement...</p>
            </div>
          ) : (
            <div className="space-y-6">
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 pb-4 border-b">
                  <Label className="text-base font-semibold whitespace-nowrap">Semaine :</Label>
                  <Select value={selectedWeek} onValueChange={setSelectedWeek}>
                    <SelectTrigger className="w-full sm:w-[400px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {weekOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Warning if Cabinet slots without location */}
                {locations.length === 0 && settings.some(s => s.slots.some(slot => slot.in_office_only)) && (
                  <Alert variant="destructive" className="border-orange-300 bg-orange-50">
                    <AlertCircle className="h-4 w-4 text-orange-600" />
                    <AlertDescription className="text-orange-800">
                      Vous avez des créneaux "Cabinet" actifs mais aucun lieu configuré.{" "}
                      <Button variant="link" className="p-0 h-auto text-orange-800 underline" onClick={() => navigate("/locations")}>
                        Ajouter un lieu
                      </Button>
                    </AlertDescription>
                  </Alert>
                )}

                {DAYS.map((day) => {
                  const daySetting = getDaySetting(day.value);
                  return (
                    <Card key={day.value} className="p-3 md:p-4">
                      <div className="space-y-3">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-3">
                          <Label className="text-base font-semibold">{day.label}</Label>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => addSlot(day.value)}
                            className="w-full sm:w-auto"
                          >
                            <Plus className="w-4 h-4 mr-2" />
                            Ajouter un créneau
                          </Button>
                        </div>

                        {daySetting.slots.map((slot, index) => (
                          <div key={slot.id} className="flex flex-col sm:flex-row sm:items-center gap-3 p-3 bg-muted/30 rounded-lg">
                            <div className="flex items-center gap-2">
                              <Switch
                                checked={slot.is_available}
                                onCheckedChange={(checked) => updateSlot(day.value, slot.id, "is_available", checked)}
                              />
                              <span className="text-sm whitespace-nowrap">
                                {slot.is_available ? "Ouvert" : "Fermé"}
                              </span>
                            </div>

                            {slot.is_available && (
                              <>
                                <div className="flex flex-col sm:flex-row sm:items-center gap-2 w-full sm:w-auto">
                                  <div className="flex items-center gap-2">
                                    <Label className="text-sm">De</Label>
                                    <Select
                                      value={slot.start_time}
                                      onValueChange={(value) => updateSlot(day.value, slot.id, "start_time", value)}
                                    >
                                      <SelectTrigger className="w-24">
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {TIME_OPTIONS.map((time) => (
                                          <SelectItem key={time} value={time}>
                                            {time}
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </div>

                                  <div className="flex items-center gap-2">
                                    <Label className="text-sm">à</Label>
                                    <Select
                                      value={slot.end_time}
                                      onValueChange={(value) => updateSlot(day.value, slot.id, "end_time", value)}
                                    >
                                      <SelectTrigger className="w-24">
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {TIME_OPTIONS.map((time) => (
                                          <SelectItem key={time} value={time}>
                                            {time}
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </div>
                                </div>

                                <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:ml-3 sm:pl-3 sm:border-l w-full sm:w-auto">
                                  {/* Checkbox pour Visio (peut être combiné avec Cabinet) */}
                                  <div className="flex items-center gap-2">
                                    <Switch
                                      checked={slot.online_only || (!slot.in_office_only && !slot.online_only)}
                                      onCheckedChange={(checked) => {
                                        if (checked) {
                                          // Si cabinet aussi coché, on garde le mode mixte
                                          if (slot.in_office_only) {
                                            updateSlot(day.value, slot.id, "online_only", false);
                                            updateSlot(day.value, slot.id, "in_office_only", false);
                                          } else {
                                            updateSlot(day.value, slot.id, "online_only", true);
                                            updateSlot(day.value, slot.id, "in_office_only", false);
                                          }
                                        } else {
                                          // Désactiver visio -> si cabinet actif, passer en cabinet only
                                          if (!slot.in_office_only) {
                                            updateSlot(day.value, slot.id, "in_office_only", true);
                                          }
                                          updateSlot(day.value, slot.id, "online_only", false);
                                        }
                                      }}
                                    />
                                    <span className="text-sm whitespace-nowrap">Visio</span>
                                  </div>

                                  {/* Checkbox pour Cabinet (peut être combiné avec Visio) */}
                                  <div className="flex items-center gap-2">
                                    <Switch
                                      checked={slot.in_office_only || (!slot.in_office_only && !slot.online_only)}
                                      onCheckedChange={(checked) => {
                                        if (checked) {
                                          // Si visio aussi coché, on passe en mode mixte
                                          if (slot.online_only) {
                                            updateSlot(day.value, slot.id, "online_only", false);
                                            updateSlot(day.value, slot.id, "in_office_only", false);
                                          } else {
                                            updateSlot(day.value, slot.id, "in_office_only", true);
                                            updateSlot(day.value, slot.id, "online_only", false);
                                          }
                                          // Set default location if not already set
                                          if (!slot.location_id && defaultLocationId) {
                                            updateSlot(day.value, slot.id, "location_id", defaultLocationId);
                                          }
                                        } else {
                                          // Désactiver cabinet -> si visio actif, passer en visio only
                                          if (!slot.online_only) {
                                            updateSlot(day.value, slot.id, "online_only", true);
                                          }
                                          updateSlot(day.value, slot.id, "in_office_only", false);
                                        }
                                      }}
                                    />
                                    <span className="text-sm whitespace-nowrap">Cabinet</span>
                                  </div>

                                  {/* Location selector - show when cabinet is enabled (in_office_only or mixed mode) */}
                                  {(slot.in_office_only || (!slot.in_office_only && !slot.online_only)) && (
                                    <div className="flex items-center gap-2 w-full sm:w-auto">
                                      <MapPin className="w-4 h-4 text-muted-foreground shrink-0" />
                                      {locations.length > 0 ? (
                                        <Select
                                          value={slot.location_id || "none"}
                                          onValueChange={(value) => updateSlot(day.value, slot.id, "location_id", value === "none" ? null : value)}
                                        >
                                          <SelectTrigger className="w-[180px]">
                                            <SelectValue placeholder="Choisir un lieu" />
                                          </SelectTrigger>
                                          <SelectContent>
                                            <SelectItem value="none">Choisir un lieu</SelectItem>
                                            {locations.map((loc) => (
                                              <SelectItem key={loc.id} value={loc.id}>
                                                {loc.name}{loc.city ? ` (${loc.city})` : ""}
                                              </SelectItem>
                                            ))}
                                          </SelectContent>
                                        </Select>
                                      ) : (
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          onClick={() => navigate("/locations")}
                                          className="gap-1 text-xs"
                                        >
                                          <Plus className="w-3 h-3" />
                                          Ajouter un lieu
                                        </Button>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </>
                            )}

                            {daySetting.slots.length > 1 && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => removeSlot(day.value, slot.id)}
                                className="ml-auto"
                              >
                                <Trash2 className="w-4 h-4 text-destructive" />
                              </Button>
                            )}
                          </div>
                        ))}
                      </div>
                    </Card>
                  );
                })}

                <div className="flex justify-end gap-4 pt-4">
                  <Button variant="outline" onClick={() => navigate("/appointments")}>
                    Annuler
                  </Button>
                  <Button onClick={handleSave}>
                    <Save className="w-4 h-4 mr-2" />
                    Sauvegarder
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
    </PageContainer>
  );
}
